// @flow
import * as React from 'react';
import {connect} from 'react-redux';
import {formActions, mergeActionsToProps} from 'redux-pure-form';
import type {MapStateToProps} from 'react-redux';

import EventContainer from './calendar.event-container.js';
import ResourceDescription from './calendar.event-resource-description.js';
import Discrepancies from './calendar.event-discrepancies.js';
import CustomFields from './calendar.event-custom-fields.js';
import Indication from '../../ui/indication.js';

import {selectUIMessages, selectCustomResources} from '../calendar.selectors.js';
import {checkIn} from '../calendar-day.actions.js';
import {
  EVENT_CHECKIN,
  EVENT_VIEW,
  AIR_EVENT_TYPE,
  MAINTENANCE_EVENT_TYPE
} from '../calendar.consts.js';

import Datepicker from "../../common/datepicker/datepicker";
import Timepicker from "../../common/timepicker/timepicker";
import DurationPicker from "../../common/durationpicker/durationpicker";
import {newDate} from "../calendar.model";
import {dateToTime} from "../../../utils/date";
import {isObjectEmpty} from "../../../utils/object";
import counterBase from "../../../utils/counterBase";
import {getFlightTime} from "./utils/flight";

type OwnProps = {||};

type MappedState = {|
  UI: UI,
  URL: URL
|};

type Props = {|
  ...OwnProps,
  ...MappedState
|};

type State = {
  actualArrivalTime: Date | null,
  enableTrackFlight: boolean
};

class EventCheckin extends React.Component<Props, State> {
  static footer = true;

  constructor(props: Props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCheckinCancel = this.handleCheckinCancel.bind(this);
    this.handleHobbsChange = this.handleHobbsChange.bind(this);
    this.handleCustomMetersChange = this.handleCustomMetersChange.bind(this);
    this.toFixed = this.toFixed.bind(this);
    const {organization, event, timezone} = this.props;
    const enableTrackFlight = organization.organizationSettings.enableTrackFlight === "1";

    this.state = {
      enableTrackFlight,
      ...(enableTrackFlight && !isObjectEmpty(event) && !isObjectEmpty(timezone)
        ? this.calculateEventTiming(event)
        : {})
    };
  }

  state = {
    departureTime: null,
    flightTime: null,
    expectedArrivalTime: null,
    actualArrivalTime: null,
    enableTrackFlight: false
  };

  calculateEventTiming = event => {
    const {timezone} = this.props;
    const eventTimezone = event && event.timeZone ? event.timeZone[event.timeZoneID] : {offset: 0};
    const departureTime = newDate(event.departureTime, eventTimezone.offset);
    let flightTime = getFlightTime(event);

    const expectedArrivalTime = newDate(event.expectedArrivalTime, eventTimezone.offset);
    const actualArrivalTime = newDate(timezone.timeNow, eventTimezone.offset);

    return {
      departureTime,
      flightTime,
      expectedArrivalTime,
      actualArrivalTime
    };
  };

  componentDidUpdate = prevProps => {
    const {event, timezone, checkinForm, onChange} = this.props;
    const {enableTrackFlight} = this.state;

    if (enableTrackFlight &&
      !isObjectEmpty(timezone) &&
      isObjectEmpty(prevProps.event) &&
      !isObjectEmpty(event)
    ) {
      this.setState({...this.calculateEventTiming(event)});
    }

    if (event.previousCheckedInEventCounters && 
        event.previousCheckedInEventCounters.length > 0 &&
        checkinForm.countersList &&
        checkinForm.countersList.length > 0 &&
        (!prevProps.checkinForm.countersList || prevProps.checkinForm.countersList.length === 0)) {
      checkinForm.countersList.forEach(id => {
        const prevCounter = event.previousCheckedInEventCounters.find(counter => counter.id === id);
        
        if (prevCounter && prevCounter.value) {
          onChange(`checkinForm.counters.${id}.newValue`, prevCounter.value);
          onChange(`checkinForm.counters.${id}.value`, prevCounter.value);
        }
      });
    }
  };

  handleSubmit(e) {
    e.preventDefault();
    const {checkIn} = this.props;
    const {actualArrivalTime, enableTrackFlight} = this.state;
    let params = {};

    if (enableTrackFlight) {
      params = {actualArrivalTime};
    }

    checkIn(params);
  }

  handleCheckinCancel() {
    const {URL, switchModals} = this.props;
    return switchModals(
      EVENT_VIEW,
      {
        ...URL.eventParams,
        action: EVENT_VIEW
      },
      URL.eventBreadcrumbs.slice(-1)
    );
  }

  handleCustomMetersChange(e) {
    const {fieldAttrs, checkinForm} = this.props;
    const {id} = e.currentTarget.dataset;

    return fieldAttrs.onChange(`checkinForm.resources.${id}`, {
      ...checkinForm.resources[id],
      diffCounter: e.currentTarget.value,
      newCounterValue: (
        parseFloat(e.currentTarget.value) + counterBase(checkinForm.resources[id].counterValue)
      ).toFixed(2)
    });
  }

  toFixed(e) {
    const {fieldAttrs, checkinForm} = this.props;
    const {id} = e.currentTarget.dataset;

    return fieldAttrs.onChange(`checkinForm.resources.${id}`, {
      ...checkinForm.resources[id],
      diffCounter: parseFloat(e.currentTarget.value).toFixed(2),
      newCounterValue: (
        parseFloat(e.currentTarget.value) + counterBase(checkinForm.resources[id].counterValue)
      ).toFixed(2)
    });
  }

  handleHobbsChange(e) {
    const {checkinForm, customResources, onChange} = this.props;
    const value = parseFloat(e.currentTarget.value);
    const {instructorMeter, resourceMeter} = checkinForm;

    const hobbsCounter = checkinForm.counters[e.currentTarget.dataset.id];
    customResources.forEach(resID => {
      const counter = checkinForm.resources[resID];
      const isInstCounter = counter.typeUnique === 'instructor';
      const hobbsValue = parseFloat(hobbsCounter.value);

      const diffCounter =
        value >= hobbsValue ? (value - hobbsValue).toFixed(2) : counter.diffCounter;
      const newCounterValue =
        value >= hobbsValue
          ? (counterBase(counter.counterValue) + value - hobbsValue).toFixed(2)
          : counter.newCounterValue;

      if (
        (isInstCounter && instructorMeter === 'hobbs') ||
        (!isInstCounter && resourceMeter === 'hobbs')
      ) {
        onChange(`checkinForm.resources.${resID}`, {
          ...counter,
          diffCounter,
          newCounterValue
        });
      }
    });

    return onChange(e.currentTarget.name, e.currentTarget.value);
  }

  onArrivalTimeChange = time => {
    const {actualArrivalTime} = this.state;
    const [hours, minutes] = time.split(":")
    actualArrivalTime.setHours(hours, minutes);

    this.setState({
      actualArrivalTime: new Date(actualArrivalTime)
    });
  };

  render() {
    const {
      accessgroups = {},
      user,
      checkinForm,
      resources,
      event,
      aircraft,
      customResources,
      fieldAttrs
    } = this.props;

    const {
      actualArrivalTime,
      departureTime,
      enableTrackFlight,
      expectedArrivalTime,
      flightTime
    } = this.state;

    const userIsAPilot = event.pilotID === user.id;
    const meterView = userIsAPilot || accessgroups.meterView;
    const counterEdit = userIsAPilot || accessgroups.counterEdit;

    const isMaintenanceEvent = event.type === MAINTENANCE_EVENT_TYPE;
    const isOutOfSequence = event.previousCheckedInEventCounters !== null;

    if (!Object.keys(checkinForm).length)
      return <Indication className="event-form-indication" messages={this.props.UIMessages} />;

    return (
      <EventContainer>
        <ResourceDescription />

        <form onSubmit={this.handleSubmit}>
          {enableTrackFlight && !isMaintenanceEvent && (
            <div className="row track-flight-form">
              <div className={"form-group col-xs-12"}>
                <label htmlFor="departureTime" className="time-label col-sm-3">
                  Departure Time
                </label>
                <div className="col-sm-9 padding-left-0">
                  <div className="row">
                    <div className="col-sm-4 col-xs-6">
                      <Datepicker
                        id={"period"}
                        dateFormat={user.settings.date}
                        selected={departureTime}
                        className="form-control inner-datepicker input-sm"
                        disabled={true}
                      />
                    </div>
                    <div className="col-xs-2 padding-left-0">
                      <Timepicker
                        value={dateToTime(departureTime)}
                        id={"departureTime"}
                        name={"departureTime"}
                        readonly={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={"form-group col-xs-12"}>
                <label htmlFor="flightTime" className="time-label col-sm-3">
                  Flight Time
                </label>
                <DurationPicker
                  value={flightTime}
                  readonly={true}
                  disabled={true}
                  id={"flightTime"}
                  name={"flightTime"}
                />
              </div>
              <div className={"form-group col-xs-12"}>
                <label htmlFor="expectedArrivalTime" className="time-label col-sm-3">
                  Expected Arrival Time
                </label>
                <Timepicker
                  value={dateToTime(expectedArrivalTime)}
                  readonly={true}
                  id={"expectedArrivalTime"}
                  name={"expectedArrivalTime"}
                />
              </div>
              <div className={"form-group col-xs-12"}>
                <label htmlFor="arrivalTime" className="time-label col-sm-3">
                  Actual Arrival Time
                </label>
                <Timepicker
                  value={dateToTime(actualArrivalTime)}
                  onChange={this.onArrivalTimeChange}
                  readonly={false}
                  required
                  id={"arrivalTime"}
                  name={"arrivalTime"}
                />
              </div>
            </div>
          )}
          {meterView && checkinForm.countersList.length ? (
            <div>
              <h5 className="discrepancies-modal-heading">Meters Values</h5>

              {isOutOfSequence ? (
                <div className="meters-update-warning panel panel-warning panel-min-height">
                  <div className="panel-heading">
                    <p className="panel-title">
                      <span className="icon warning" /> The updated meter values will not affect inspection tracking
                    </p>
                  </div>
                </div>
              ) : ""}

              <div className="table-meters-wrap">
                <table className="table table-md table-panel meters-table">
                  <thead>
                    <tr>
                      <td>
                        <span className="text-muted">Meters</span>
                      </td>
                      <td width="100px" className="text-right">
                        <span className="text-muted">Start</span>
                      </td>
                      <td width="95px" className="text-center">
                        <span className="text-muted">End</span>
                      </td>
                      <td width="100px" className="text-right">
                        <span className="text-muted">Total</span>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {checkinForm.countersList.map(id => {
                      const counter = checkinForm.counters[id];
                      const isHobbs =
                        (checkinForm.instructorMeter === 'hobbs' ||
                          checkinForm.resourceMeter === 'hobbs') &&
                        customResources.length &&
                        counter.name === 'Hobbs';

                      let correctCounterValue = counter.value;

                      if (event.previousCheckedInEventCounters !== null) {
                        const prevCounter = event.previousCheckedInEventCounters.find(counter => counter.id === id);

                        if (prevCounter && prevCounter.value) {
                          correctCounterValue = prevCounter.value;
                        }
                      }

                      return (
                        <tr key={id}>
                          <td>
                            <div className="compact">
                              <b>{counter.name}</b>
                            </div>
                          </td>
                          <td className="text-right">
                            <span children={correctCounterValue}/>
                          </td>
                          <td className="text-right">
                            {counterEdit ? (
                              <input
                                type="text"
                                className="form-control text-right"
                                size="10"
                                maxLength="10"
                                pattern="\d*\.?\d*"
                                title="Enter a number"
                                name={`checkinForm.counters.${id}.newValue`}
                                value={checkinForm.counters[id].newValue || ''}
                                data-id={id}
                                onChange={isHobbs ? this.handleHobbsChange : this.props.onChange}
                              />
                            ) : (
                              <span className="d-block text-center">
                                {checkinForm.counters[id].newValue || ''}
                              </span>
                            )}
                          </td>
                          <td className="text-right">
                            {counter.type === 'hobbs' || counter.type === 'tachometer' ? (
                              <span>
                                {' '}
                                {parseFloat(counter.newValue) >= parseFloat(counter.value)
                                  ? (
                                      parseFloat(counter.newValue) - parseFloat(counter.value)
                                    ).toFixed(2)
                                  : '0.00'}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}

                    {customResources.length
                      ? customResources.map(resID => {
                          const res = checkinForm.resources[resID];
                          return (
                            <tr key={`custom-${resID}`}>
                              <td>
                                <div className="compact">
                                  <b>
                                    {res.name} {res.typeUnique}
                                  </b>
                                </div>
                              </td>
                              <td />
                              <td />
                              <td className="text-right">
                                <input
                                  type="text"
                                  className="form-control text-right"
                                  size="10"
                                  maxLength="10"
                                  pattern="\d*\.?\d*"
                                  onBlur={this.toFixed}
                                  title="Enter a number"
                                  data-id={resID}
                                  value={res.diffCounter}
                                  onChange={this.handleCustomMetersChange}
                                />
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-muted">
              <br />
              No meters defined for aircraft
            </p>
          )}
          <h5>Event fields</h5>
          {event.type === AIR_EVENT_TYPE ? <CustomFields formName="checkinForm" /> : null}
          <div className="form-group">
            <label
              htmlFor="comments"
              className={parseInt(checkinForm.enforseComments, 10) === 1 ? 'required-label' : ''}
            >
              Comments
            </label>
            <textarea
              id="comments"
              name="checkinForm.comments"
              required={parseInt(checkinForm.enforseComments, 10) === 1}
              value={checkinForm.comments || ''}
              className="form-control input-sm"
              rows="4"
              {...fieldAttrs}
            />
          </div>
          <input id={EVENT_CHECKIN} type="submit" style={{ display: 'none' }} />
          <input
            id={`${EVENT_CHECKIN}-cancel`}
            onClick={this.handleCheckinCancel}
            style={{ display: 'none' }}
          />
        </form>

        {resources[aircraft.resourceID] && resources[aircraft.resourceID].resourceTypeName === 'Aircraft' ? (
          <Discrepancies switchModals={this.props.switchModals} />
        ) : null}
      </EventContainer>
    );
  }
}

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  UIMessages: selectUIMessages(EVENT_CHECKIN)(state),
  URL: state.URL,
  user: state.user,
  organization: state.organization,
  accessgroups: state.accessgroups,
  event: state.event,
  aircraft: state.aircraft,
  resources: state.resources,
  checkinForm: state.checkinForm,
  timezone: state.timezone,
  customResources: selectCustomResources(state)
});

export default connect(
  mapStateToProps,
  {
    checkIn,
    ...formActions
  },
  mergeActionsToProps
)(EventCheckin);
